import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Search, X, Camera, Lightbulb, Truck, Video } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { SectionReveal } from '@/components/shared/SectionReveal';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getEquipment } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { SEO } from '@/components/SEO';

const equipmentCategories = [
  { value: 'all', label: 'All Equipment' },
  { value: 'camera', label: 'Cameras' },
  { value: 'lenses', label: 'Lenses' },
  { value: 'stabilization', label: 'Stabilization' },
  { value: 'lighting', label: 'Lighting' },
  { value: 'grip', label: 'Grip & Transport' },
  { value: 'drones', label: 'Drones' },
  { value: 'dit', label: 'DIT / Monitoring' },
];

export default function RentalsPage() {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [category, setCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [brandFilter, setBrandFilter] = useState('all-brands');
  const [specFilter, setSpecFilter] = useState('all-specs');
  const [useCaseFilter, setUseCaseFilter] = useState('all-use-cases');

  // Extract unique filter options from equipment data
  const [brands, setBrands] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [useCases, setUseCases] = useState([]);

  const fetchEquipment = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        category: category !== 'all' ? category : undefined,
        brand: (brandFilter && brandFilter !== 'all-brands') ? brandFilter : undefined,
        spec: (specFilter && specFilter !== 'all-specs') ? specFilter : undefined,
        use_case: (useCaseFilter && useCaseFilter !== 'all-use-cases') ? useCaseFilter : undefined
      };
      const data = await getEquipment(filters);
      setEquipment(data);

      // Extract unique values for filters
      const uniqueBrands = [...new Set(data.map(item => item.brand).filter(Boolean))].sort();
      const uniqueSpecs = [...new Set(data.map(item => item.spec_category).filter(Boolean))].sort();
      const uniqueUseCases = [...new Set(data.map(item => item.use_case).filter(Boolean))].sort();
      
      setBrands(uniqueBrands);
      setSpecs(uniqueSpecs);
      setUseCases(uniqueUseCases);
    } catch (err) {
      console.error('Failed to fetch equipment:', err);
    } finally {
      setLoading(false);
    }
  }, [category, brandFilter, specFilter, useCaseFilter]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  // Client-side search filter
  const filteredEquipment = equipment.filter(item =>
    searchQuery === '' ||
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.brand.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const clearAllFilters = () => {
    setCategory('all');
    setSearchQuery('');
    setBrandFilter('all-brands');
    setSpecFilter('all-specs');
    setUseCaseFilter('all-use-cases');
  };

  const hasActiveFilters = category !== 'all' || searchQuery !== '' || (brandFilter && brandFilter !== 'all-brands') || (specFilter && specFilter !== 'all-specs') || (useCaseFilter && useCaseFilter !== 'all-use-cases');

  const handleContactClick = (itemName) => {
    // Navigate to contact page with pre-filled inquiry about this equipment
    navigate('/contact', { 
      state: { 
        project_type: 'rentals',
        message: `I'm interested in renting: ${itemName}\n\nPlease provide details about availability, rates, and package options.`
      } 
    });
  };

  return (
    <div>
      <SEO 
        title="Equipment Rentals — Camera, Lighting, Grip & Stabilization | Baklight Mexico"
        description="Professional camera systems, lighting packages, grip equipment, and stabilization for film production in Mexico. ARRI, RED, Sony, Canon, DJI, Aputure. Contact for quote."
        path="/rentals"
      />
      
      <PageHeader
        meta="Equipment & Rentals"
        title="Infrastructure built for cinema."
        subtitle="Camera systems, grip trucks, lighting, stabilization, and DIT packages — all maintained, tested, and ready for professional production. Contact us for custom quotes."
      />

      <section className="pb-20 sm:pb-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          
          {/* ———— FEATURED PACKAGES ———— */}
          <div className="mb-16">
            <SectionReveal>
              <h2 className="font-display text-3xl md:text-4xl text-[hsl(var(--bk-fg))] mb-3">Featured Packages</h2>
              <p className="font-body text-[hsl(var(--bk-fg-muted))] mb-8">Complete solutions tailored to your production needs</p>
            </SectionReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {/* Camera Package */}
              <SectionReveal delay={0.1}>
                <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6 hover:border-[hsl(var(--bk-fg-muted))] transition-colors group h-full flex flex-col">
                  <div className="w-16 h-16 rounded-full bg-[hsl(var(--bk-fg))]/10 flex items-center justify-center mb-4 group-hover:bg-[hsl(var(--bk-fg))]/20 transition-colors">
                    <Camera className="w-8 h-8 text-[hsl(var(--bk-fg))]" />
                  </div>
                  <h3 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-3">Camera Package</h3>
                  <ul className="space-y-2 mb-6 text-sm text-[hsl(var(--bk-fg-muted))] flex-grow">
                    <li>• Selected camera (Sony, Canon, RED, Alexa)</li>
                    <li>• Rigging cage & accessories</li>
                    <li>• Media (memory cards)</li>
                    <li>• Batteries & power solutions</li>
                    <li>• Lens mounts & adapters</li>
                    <li>• Monitor solution</li>
                    <li>• Focus system, matte box, filters</li>
                    <li>• Tripod & support</li>
                    <li>• Selected lens kit</li>
                  </ul>
                  <button 
                    onClick={() => navigate('/contact')}
                    className="w-full px-4 py-2 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-md hover:opacity-90 transition-opacity font-body text-sm"
                  >
                    Request Quote
                  </button>
                </div>
              </SectionReveal>

              {/* Lighting Package */}
              <SectionReveal delay={0.2}>
                <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6 hover:border-[hsl(var(--bk-fg-muted))] transition-colors group h-full flex flex-col">
                  <div className="w-16 h-16 rounded-full bg-[hsl(var(--bk-fg))]/10 flex items-center justify-center mb-4 group-hover:bg-[hsl(var(--bk-fg))]/20 transition-colors">
                    <Lightbulb className="w-8 h-8 text-[hsl(var(--bk-fg))]" />
                  </div>
                  <h3 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-3">Lighting Package</h3>
                  <ul className="space-y-2 mb-6 text-sm text-[hsl(var(--bk-fg-muted))] flex-grow">
                    <li>• Select light source (LED, Tungsten, Fluorescent, HMI)</li>
                    <li>• Define your footprint</li>
                    <li>• Matched grip equipment</li>
                    <li>• Stands, sandbags, stingers</li>
                    <li>• Grip & light control</li>
                    <li>• Custom solutions from catalog</li>
                  </ul>
                  <button 
                    onClick={() => navigate('/contact')}
                    className="w-full px-4 py-2 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-md hover:opacity-90 transition-opacity font-body text-sm"
                  >
                    Request Quote
                  </button>
                </div>
              </SectionReveal>

              {/* Grip Truck Package */}
              <SectionReveal delay={0.3}>
                <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6 hover:border-[hsl(var(--bk-fg-muted))] transition-colors group h-full flex flex-col">
                  <div className="w-16 h-16 rounded-full bg-[hsl(var(--bk-fg))]/10 flex items-center justify-center mb-4 group-hover:bg-[hsl(var(--bk-fg))]/20 transition-colors">
                    <Truck className="w-8 h-8 text-[hsl(var(--bk-fg))]" />
                  </div>
                  <h3 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-3">Grip Truck Package</h3>
                  <ul className="space-y-2 mb-6 text-sm text-[hsl(var(--bk-fg-muted))] flex-grow">
                    <li>• Complete on-set convenience</li>
                    <li>• Accommodate lighting selection</li>
                    <li>• Serve as camera van</li>
                    <li>• Everything you need on location</li>
                    <li>• Professional grip equipment</li>
                    <li>• Turnkey solution</li>
                  </ul>
                  <button 
                    onClick={() => navigate('/contact')}
                    className="w-full px-4 py-2 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-md hover:opacity-90 transition-opacity font-body text-sm"
                  >
                    Request Quote
                  </button>
                </div>
              </SectionReveal>

              {/* Documentary Package */}
              <SectionReveal delay={0.4}>
                <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6 hover:border-[hsl(var(--bk-fg-muted))] transition-colors group h-full flex flex-col">
                  <div className="w-16 h-16 rounded-full bg-[hsl(var(--bk-fg))]/10 flex items-center justify-center mb-4 group-hover:bg-[hsl(var(--bk-fg))]/20 transition-colors">
                    <Video className="w-8 h-8 text-[hsl(var(--bk-fg))]" />
                  </div>
                  <h3 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-3">Documentary Package</h3>
                  <ul className="space-y-2 mb-6 text-sm text-[hsl(var(--bk-fg-muted))] flex-grow">
                    <li>• Shooting in Mexico? We've got you covered</li>
                    <li>• Camera equipment</li>
                    <li>• Professional sound</li>
                    <li>• Drone capabilities</li>
                    <li>• Grip & lighting for your style</li>
                    <li>• Tailored to your needs</li>
                  </ul>
                  <button 
                    onClick={() => navigate('/contact')}
                    className="w-full px-4 py-2 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-md hover:opacity-90 transition-opacity font-body text-sm"
                  >
                    Request Quote
                  </button>
                </div>
              </SectionReveal>
            </div>
          </div>

          {/* ———— EQUIPMENT CATALOG ———— */}
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-[hsl(var(--bk-fg))] mb-6">Equipment Catalog</h2>
          
          {/* Search & Filters */}
          <div className="mb-10 space-y-6">
            {/* Search Bar */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--bk-fg-subtle))]" size={18} />
              <Input
                type="text"
                placeholder="Search equipment..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] focus:border-[hsl(var(--bk-accent))]"
                data-testid="equipment-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--bk-fg-subtle))] hover:text-[hsl(var(--bk-fg))]"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Category Tabs */}
            <div className="overflow-x-auto">
              <Tabs value={category} onValueChange={setCategory} data-testid="equipment-category-tabs">
                <TabsList className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] flex-nowrap">
                  {equipmentCategories.map((cat) => (
                    <TabsTrigger
                      key={cat.value}
                      value={cat.value}
                      className="font-mono-meta whitespace-nowrap data-[state=active]:bg-[hsl(var(--bk-fg))] data-[state=active]:text-[hsl(var(--bk-bg))]"
                    >
                      {cat.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            {/* 3-Tier Filters: Brand, Spec, Use Case */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2 block">Filter by Brand</label>
                <Select value={brandFilter} onValueChange={(v) => setBrandFilter(v === 'all-brands' ? '' : v)}>
                  <SelectTrigger className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))]" data-testid="brand-filter">
                    <SelectValue placeholder="All Brands" />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[50]">
                    <SelectItem value="all-brands">All Brands</SelectItem>
                    {brands.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2 block">Filter by Spec</label>
                <Select value={specFilter} onValueChange={(v) => setSpecFilter(v === 'all-specs' ? '' : v)}>
                  <SelectTrigger className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))]" data-testid="spec-filter">
                    <SelectValue placeholder="All Specs" />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[50]">
                    <SelectItem value="all-specs">All Specs</SelectItem>
                    {specs.map(spec => (
                      <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-2 block">Filter by Use Case</label>
                <Select value={useCaseFilter} onValueChange={(v) => setUseCaseFilter(v === 'all-use-cases' ? '' : v)}>
                  <SelectTrigger className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))]" data-testid="use-case-filter">
                    <SelectValue placeholder="All Use Cases" />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[50]">
                    <SelectItem value="all-use-cases">All Use Cases</SelectItem>
                    {useCases.map(useCase => (
                      <SelectItem key={useCase} value={useCase}>{useCase}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters Display */}
            {hasActiveFilters && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">Active filters:</span>
                {category !== 'all' && (
                  <Badge variant="secondary" className="bg-[hsl(var(--bk-accent))]/10 text-[hsl(var(--bk-accent))] border-[hsl(var(--bk-accent))]/20">
                    {equipmentCategories.find(c => c.value === category)?.label}
                  </Badge>
                )}
                {brandFilter && brandFilter !== 'all-brands' && (
                  <Badge variant="secondary" className="bg-[hsl(var(--bk-accent))]/10 text-[hsl(var(--bk-accent))] border-[hsl(var(--bk-accent))]/20">
                    Brand: {brandFilter}
                  </Badge>
                )}
                {specFilter && specFilter !== 'all-specs' && (
                  <Badge variant="secondary" className="bg-[hsl(var(--bk-accent))]/10 text-[hsl(var(--bk-accent))] border-[hsl(var(--bk-accent))]/20">
                    Spec: {specFilter}
                  </Badge>
                )}
                {useCaseFilter && useCaseFilter !== 'all-use-cases' && (
                  <Badge variant="secondary" className="bg-[hsl(var(--bk-accent))]/10 text-[hsl(var(--bk-accent))] border-[hsl(var(--bk-accent))]/20">
                    Use: {useCaseFilter}
                  </Badge>
                )}
                <button
                  onClick={clearAllFilters}
                  className="font-mono-meta text-xs text-[hsl(var(--bk-accent))] hover:opacity-80 transition-opacity underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Equipment Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="rounded-lg border border-[hsl(var(--bk-line))] p-6">
                  <Skeleton className="h-6 w-3/4 mb-3" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredEquipment.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-body text-[hsl(var(--bk-fg-muted))] mb-4">No equipment found matching your filters.</p>
              <button
                onClick={clearAllFilters}
                className="font-body text-sm text-[hsl(var(--bk-accent))] hover:opacity-80 transition-opacity"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6 font-mono-meta text-sm text-[hsl(var(--bk-fg-subtle))]">
                Showing {filteredEquipment.length} item{filteredEquipment.length !== 1 ? 's' : ''}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredEquipment.map((item, i) => (
                  <SectionReveal key={item.id || item.name} delay={i * 0.04}>
                    <div className="p-6 rounded-lg border border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg-elev))] h-full flex flex-col" data-testid={`equipment-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      {/* NO IMAGE - Images removed per user request */}

                      {/* Category & Package Badge */}
                      <div className="flex items-center gap-2 mb-2">
                        <p className="font-mono-meta text-xs text-[hsl(var(--bk-accent))]">
                          {item.category.toUpperCase()}
                        </p>
                        {item.is_package && (
                          <Badge variant="secondary" className="bg-[hsl(var(--bk-success))]/10 text-[hsl(var(--bk-success))] border-[hsl(var(--bk-success))]/20 text-xs">
                            PACKAGE
                          </Badge>
                        )}
                      </div>

                      {/* Equipment Name */}
                      <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-2">{item.name}</h3>
                      
                      {/* Brand & Spec Category */}
                      {(item.brand || item.spec_category) && (
                        <div className="flex items-center gap-2 mb-3 text-xs">
                          {item.brand && (
                            <span className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">{item.brand}</span>
                          )}
                          {item.brand && item.spec_category && (
                            <span className="text-[hsl(var(--bk-fg-subtle))]">•</span>
                          )}
                          {item.spec_category && (
                            <span className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">{item.spec_category}</span>
                          )}
                        </div>
                      )}

                      {/* Description */}
                      <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed mb-4">{item.description}</p>
                      
                      {/* Specs */}
                      {item.specs && item.specs.length > 0 && (
                        <div className="mb-4">
                          <details className="group">
                            <summary className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] cursor-pointer hover:text-[hsl(var(--bk-accent))] transition-colors list-none flex items-center gap-2">
                              <span className="group-open:rotate-90 transition-transform">▶</span>
                              Specifications
                            </summary>
                            <div className="mt-3 space-y-1.5 pl-4">
                              {item.specs.slice(0, 6).map((spec, j) => (
                                <div key={j} className="flex items-start gap-2">
                                  <div className="w-1 h-1 rounded-full bg-[hsl(var(--bk-accent))] mt-1.5 flex-shrink-0" />
                                  <span className="font-mono-meta text-[hsl(var(--bk-fg-muted))]" style={{ textTransform: 'none', letterSpacing: 'normal', fontSize: '12px' }}>
                                    {spec}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </details>
                        </div>
                      )}

                      {/* Use Case Tags */}
                      {item.use_case && (
                        <div className="mb-4 flex flex-wrap gap-1.5">
                          {item.use_case.split(',').map((useCase, idx) => (
                            <Badge 
                              key={idx} 
                              variant="outline" 
                              className="text-xs font-mono-meta border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg-subtle))]"
                            >
                              {useCase.trim()}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Availability & Contact Button */}
                      <div className="mt-auto pt-4 border-t border-[hsl(var(--bk-line))] space-y-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${item.available ? 'bg-[hsl(var(--bk-success))]' : 'bg-[hsl(var(--bk-danger))]'}`} />
                          <span className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">
                            {item.available ? 'Available' : 'Currently Booked'}
                          </span>
                        </div>

                        <button
                          onClick={() => handleContactClick(item.name)}
                          className="w-full font-body text-sm px-4 py-2.5 bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                          data-testid={`equipment-contact-button-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          Contact for Quote <ArrowRight size={14} />
                        </button>
                      </div>
                    </div>
                  </SectionReveal>
                ))}
              </div>
            </>
          )}
          </div> {/* End Equipment Catalog */}

        </div> {/* End max-w container */}
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 text-center">
          <SectionReveal>
            <h2 className="font-display text-3xl md:text-4xl tracking-[-0.02em] text-[hsl(var(--bk-fg))] mb-4">
              Need a custom equipment package?
            </h2>
            <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] max-w-[600px] mx-auto mb-8">
              We build tailored packages for every production. Package deals include significant discounts. Tell us what you need.
            </p>
            <button
              onClick={() => navigate('/contact', { state: { project_type: 'rentals' } })}
              data-testid="equipment-request-quote-button"
              className="inline-flex items-center gap-2 font-body text-sm px-8 py-3 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity active:scale-[0.98]"
            >
              Request Custom Quote <ArrowRight size={16} />
            </button>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
